import { type MutableRefObject } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { ChipSelectMulti } from '@/shared/ui/components/form/ChipSelect';
import { Text, NativeCheckbox } from '@/shared/ui';
import { TextInput } from '@/shared/ui/components/form/inputs';
import { PublicationContentSection } from './PublicationContentSection';
import { PhotoUploadSection } from './PhotoUploadSection';
import { VideoSection } from './VideoSection';
import { RentalDetailsSection } from './RentalDetailsSection';
import { OwnershipSection } from './OwnershipSection';
import { AssignmentSection } from './AssignmentSection';
import { DocumentationSection } from './DocumentationSection';
import { MaskedIntegerField, MaskedPercentageField } from './MaskedNumberField';
import { RENTAL_GUARANTEE_OPTIONS, PROPERTY_FIELD_LIMITS } from './schema';
import type { PropertyDrawerSection } from './drawerSections';
import type { PropertyFormValues } from './schema';

interface DescricaoTabProps {
  form: UseFormReturn<PropertyFormValues>;
  pendingImagesRef: MutableRefObject<File[]>;
  agencyId: string;
  propertyId?: string;
  existingImageKeys?: string[];
  onImageOrderChange?: (urls: string[]) => void;
  showMobileHeading?: boolean;
  syncOrPublish?: boolean;
  onSyncOrPublishChange?: (v: boolean) => void;
  hasActivePublications?: boolean;
  showPortalActions?: boolean;
  /** Section the drawer was opened onto, while it is open. */
  focusSection?: PropertyDrawerSection;
}

/**
 * Value stays the API enum token; the label is translated so the chip does
 * not surface raw tokens like `Deposito_Caucao` to the user.
 */
const buildRentalGuaranteeOptions = (t: (key: string) => string) =>
  RENTAL_GUARANTEE_OPTIONS.map(r => ({ value: r, label: t(`form.options.rentalGuaranteeType.${r}`) }));

/**
 * Right column of the property drawer form. Renders publication content,
 * photos, media, rental conditions, and operational sections (ownership,
 * assignment, documentation).
 */
export function DescricaoTab({ form, pendingImagesRef, agencyId, propertyId, existingImageKeys, onImageOrderChange, showMobileHeading, syncOrPublish, onSyncOrPublishChange, hasActivePublications, showPortalActions, focusSection }: DescricaoTabProps) {
  const { t } = useTranslation('features/properties');
  const transactionType = form.watch('transaction_type');
  const hasRental = transactionType?.includes('Aluguel');

  const content = (
    <div className="flex flex-col gap-(--space-6)">
      <div data-drawer-section="description">
        <PublicationContentSection form={form} agencyId={agencyId} />
      </div>

      {hasRental && (
        <FormSection title={t('propertyDrawer.publication.rentalConditions')}>
          <Controller
            name="rental_guarantee"
            control={form.control}
            render={({ field }) => (
              <ChipSelectMulti
                label={t('propertyDrawer.publication.rentalGuarantee')}
                options={buildRentalGuaranteeOptions(t)}
                value={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <Controller
              name="admin_percentage"
              control={form.control}
              render={({ field }) => (
                <MaskedPercentageField
                  label={t('propertyDrawer.publication.adminFee')}
                  suffix="%"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="lease_term_in_months"
              control={form.control}
              render={({ field }) => (
                <MaskedIntegerField
                  label={t('propertyDrawer.publication.term')}
                  suffix={t('propertyDrawer.publication.monthsSuffix')}
                  maxDigits={PROPERTY_FIELD_LIMITS.leaseTermMonths.maxDigits}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </FormSection>
      )}

      <PhotoUploadSection
        pendingImagesRef={pendingImagesRef}
        agencyId={agencyId}
        propertyId={propertyId}
        fallbackImageKeys={existingImageKeys}
        onImageOrderChange={onImageOrderChange}
      />

      <div data-drawer-section="videos">
        <VideoSection form={form} agencyId={agencyId} propertyId={propertyId} />
      </div>

      <FormSection title={t('propertyDrawer.media.title')}>
        <Controller
          name="virtual_tour_url"
          control={form.control}
          render={({ field }) => (
            <TextInput
              label={t('propertyDrawer.media.virtualTourUrl')}
              value={field.value ?? ''}
              placeholder={t('propertyDrawer.media.virtualTourPlaceholder')}
              onChange={field.onChange}
            />
          )}
        />
      </FormSection>

      <RentalDetailsSection form={form} />
      <div data-drawer-section="ownership">
        <OwnershipSection form={form} agencyId={agencyId} expand={focusSection === 'ownership'} />
      </div>
      <AssignmentSection form={form} />
      <div data-drawer-section="documentation">
        <DocumentationSection form={form} />
      </div>

      {propertyId && showPortalActions && (
        <Text
          as="label"
          className="flex items-center gap-(--space-2) cursor-pointer select-none text-(--text-secondary)"
          data-testid="drawer-sync-or-publish-label"
        >
          <NativeCheckbox
            data-testid="drawer-sync-or-publish-checkbox"
            checked={syncOrPublish ?? true}
            onChange={e => onSyncOrPublishChange?.(e.target.checked)}
          />
          {hasActivePublications
            ? t('propertyDrawer.publication.syncPosts')
            : t('propertyDrawer.publication.publishAfterSave')}
        </Text>
      )}
    </div>
  );

  if (showMobileHeading) {
    return (
      <FormSection title={t('form.drawer.tabs.descricao')} divider>
        {content}
      </FormSection>
    );
  }

  return content;
}
